import { Routes, Route, Navigate, Link } from 'react-router-dom';
import { Result } from 'antd';
import { useSession } from './stores/session';
import AppLayout from './components/AppLayout';
import HomePage from './pages/home';
import LoginPage from './pages/login';
import WecomSidebarPage from './pages/wecom/sidebar';
import AuditLogsPage from './pages/audit-logs';
import AdminsPage from './pages/admins';
import AiTasksPage from './pages/ai-tasks';
import PromptsPage from './pages/prompts';
import ChapterRollbackPage from './pages/chapter-rollback';
import NorthStarPage from './pages/dashboard';
import PrintDashboardPage from './pages/dashboard/print';
import ContentDashboardPage from './pages/dashboard/content';
import CreationFunnelPage from './pages/dashboard/creation-funnel';
import OrdersListPage from './pages/orders/list';
import OrderDetailPage from './pages/orders/detail';
import RefundsPage from './pages/refunds';
import TicketsPage from './pages/tickets';
import UsersSearchPage from './pages/users/search';
import UsersListAllPage from './pages/users/list-all';
import UsersDetailPage from './pages/users/detail';
import LifecyclePage from './pages/lifecycle';
import PackagesPage from './pages/packages';
import BoostPage from './pages/boost';
import VouchersPage from './pages/vouchers';
import VoucherRequestsPage from './pages/vouchers/requests';
import RedeemCodesPage from './pages/redeem-codes';
import PartnersPage from './pages/partners';
import SettlementsPage from './pages/settlements';
import SharesPage from './pages/shares';
import ReconciliationPage from './pages/reconciliation';
import FeatureFlagsPage from './pages/feature-flags';
import AdvisorCustomersPage from './pages/advisor/customers';
import AdvisorCustomerDetailPage from './pages/advisor/customers/detail';
import AdvisorOverlapsPage from './pages/advisor/overlaps';
import AdvisorSalesPage from './pages/advisor/sales';
import SalesAccountsPage from './pages/advisor/sales-accounts';
import AdvisorSettingsPage from './pages/advisor/settings';

/** Liveness stub — kept at /healthz. */
function HealthPage() {
  return <Result status="success" title="admin-web 已就绪" subTitle="经年叙 · 运营后台" />;
}

function NotFoundPage() {
  return (
    <Result status="404" title="404" subTitle="页面不存在" extra={<Link to="/">返回首页</Link>} />
  );
}

/** Guard: redirects unauthenticated visitors to /login. */
function RequireAuth({ children }: { children: React.ReactNode }) {
  const accessToken = useSession((s) => s.accessToken);
  if (!accessToken) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      {/* 企微聊天侧边栏：独立页面，不走主布局与扫码登录（自己做企微静默授权） */}
      <Route path="/wecom/sidebar" element={<WecomSidebarPage />} />

      {/* 受保护路由：统一套侧边导航壳（ProLayout）*/}
      <Route
        element={
          <RequireAuth>
            <AppLayout />
          </RequireAuth>
        }
      >
        <Route path="/" element={<HomePage />} />
        <Route path="/healthz" element={<HealthPage />} />

        {/* 系统 */}
        <Route path="/admins" element={<AdminsPage />} />
        <Route path="/feature-flags" element={<FeatureFlagsPage />} />
        <Route path="/audit-logs" element={<AuditLogsPage />} />

        {/* Stream 5：看板 */}
        <Route path="/dashboard" element={<Navigate to="/dashboard/north-star" replace />} />
        <Route path="/dashboard/north-star" element={<NorthStarPage />} />
        <Route path="/dashboard/print" element={<PrintDashboardPage />} />
        <Route path="/dashboard/content" element={<ContentDashboardPage />} />
        <Route path="/dashboard/creation-funnel" element={<CreationFunnelPage />} />

        {/* Stream 2：订单 / 退款 / 工单 / 用户 / lifecycle */}
        <Route path="/orders" element={<OrdersListPage />} />
        <Route path="/orders/:id" element={<OrderDetailPage />} />
        <Route path="/refunds" element={<RefundsPage />} />
        <Route path="/tickets" element={<TicketsPage />} />
        <Route path="/users" element={<UsersSearchPage />} />
        <Route path="/users/list-all" element={<UsersListAllPage />} />
        <Route path="/users/:userId" element={<UsersDetailPage />} />
        <Route path="/lifecycle" element={<LifecyclePage />} />

        {/* Stream 3：内容审核 */}
        <Route path="/ai-tasks" element={<AiTasksPage />} />
        <Route path="/prompts" element={<PromptsPage />} />
        <Route path="/chapter-rollback" element={<ChapterRollbackPage />} />

        {/* Stream 4：增长 / 财务 */}
        <Route path="/packages" element={<PackagesPage />} />
        <Route path="/boost" element={<BoostPage />} />
        <Route path="/vouchers" element={<VouchersPage />} />
        <Route path="/vouchers/requests" element={<VoucherRequestsPage />} />
        <Route path="/redeem-codes" element={<RedeemCodesPage />} />
        <Route path="/advisor/customers" element={<AdvisorCustomersPage />} />
        <Route path="/advisor/customers/:id" element={<AdvisorCustomerDetailPage />} />
        <Route path="/advisor/overlaps" element={<AdvisorOverlapsPage />} />
        <Route path="/advisor/sales" element={<AdvisorSalesPage />} />
        <Route path="/advisor/sales-accounts" element={<SalesAccountsPage />} />
        <Route path="/advisor/settings" element={<AdvisorSettingsPage />} />
        <Route path="/partners" element={<PartnersPage />} />
        <Route path="/settlements" element={<SettlementsPage />} />
        <Route path="/shares" element={<SharesPage />} />
        <Route path="/reconciliation" element={<ReconciliationPage />} />
      </Route>

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
